import { Fragment, useEffect, useState } from 'react'
import { Listbox, Transition } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'

/**
 * ⚠️ THIS PROJECT LOADS ONLY `@tailwind utilities` — THERE IS NO BASE LAYER.
 *
 * That is why the panel below carries explicit inline shadows and spelled-out
 * borders instead of `shadow-lg ring-1 ring-black/5`, which is what it used and
 * which silently did nothing:
 *
 *   Tailwind's `shadow-*` and `ring-*` compile to
 *     box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow)
 *   and the fallback values for those three custom properties are declared in
 *   the BASE layer. With no base layer they are undefined, the whole
 *   declaration is invalid, and the browser drops it. Measured on the live page:
 *   `boxShadow: "none"`, `border: "0px none"` — a white panel on a near-white
 *   page with no edge at all, which is exactly how it looked.
 *
 * `border` is spelled out as `border border-solid border-gray-200` for the same
 * reason: without preflight there is no global `border-style: solid`, so a bare
 * `border` class sets a width against no style and draws nothing.
 *
 * The same trap is already documented in CLAUDE.md for bare `<button>` elements.
 */

// Brand, rather than the Headless UI example palette this started from. Amber
// on amber gave the selected row no relationship to anything else on the page.
const BRAND = '#E53E14';
const BRAND_TINT = '#FFF5F3';

/* Explicit because `shadow-lg` cannot work here — see the note above. */
const PANEL_SHADOW = '0 10px 24px -6px rgba(16, 24, 40, 0.18), 0 4px 8px -4px rgba(16, 24, 40, 0.10)';

const DropDown = ({ dataList, firstSelected, selected, setSelected }) => {

    useEffect(() => {
        if (firstSelected) {
            setSelected(firstSelected);
        }
    }, [firstSelected]);

    return (
        <div className="w-60">
            <Listbox value={selected} onChange={setSelected}>
                {({ open }) => (
                    <div className="relative">
                        <Listbox.Button
                            className={`relative w-full cursor-pointer rounded-md border border-solid bg-white py-2 pl-3 pr-10 text-left text-gray-900 outline-0 focus:outline-none sm:text-sm ${
                                open ? 'border-gray-400' : 'border-gray-300'
                            }`}
                            style={{ boxShadow: open ? `0 0 0 3px ${BRAND_TINT}` : '0 1px 2px rgba(16,24,40,0.06)' }}
                        >
                            <span className="block truncate capitalize font-medium">
                                {selected?.companyName || 'Select a company'}
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                            </span>
                        </Listbox.Button>

                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            {/* z-20, because the panel is absolutely positioned with no
                                stacking context of its own: the cards below it paint in
                                document order and would otherwise win. */}
                            <Listbox.Options
                                className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border border-solid border-gray-200 bg-white py-1 capitalize focus:outline-none sm:text-sm"
                                style={{ boxShadow: PANEL_SHADOW }}
                            >
                                {dataList.map((data, index) => (
                                    <Listbox.Option
                                        key={data?._id}
                                        /*
                                         * ⚠️ STATE-DEPENDENT STYLING GOES IN `className`, NOT `style`.
                                         * Headless UI supports a render prop for className only; a
                                         * function passed to `style` is stringified and React throws
                                         * "The `style` prop expects a mapping from style properties
                                         * to values, not a string", taking the whole page with it.
                                         *
                                         * The colours are Tailwind arbitrary values rather than the
                                         * BRAND constants above because they must appear as literal
                                         * text for the JIT to generate them.
                                         */
                                        className={({ active, selected: isSelected }) =>
                                            // A hairline between rows, so two similar company
                                            // names do not read as one block of text.
                                            `relative cursor-pointer select-none py-2.5 pl-10 pr-4 capitalize border-0 border-solid ${
                                                index > 0 ? 'border-t border-gray-100' : ''
                                            } ${
                                                // Hover/keyboard focus is the stronger tint; the
                                                // current value is carried by the tick and the
                                                // weight, so the two states stay distinct.
                                                active
                                                    ? 'bg-[#FFF5F3] text-[#E53E14]'
                                                    : isSelected
                                                        ? 'bg-[#FDFAF9] text-[#E53E14]'
                                                        : 'bg-white text-gray-700'
                                            }`
                                        }
                                        value={data}
                                    >
                                        {({ selected: isSelected }) => (
                                            <>
                                                <span
                                                    className={`block truncate capitalize ${
                                                        isSelected ? 'font-semibold' : 'font-normal'
                                                    }`}
                                                >
                                                    {data?.companyName}
                                                </span>
                                                {isSelected ? (
                                                    <span
                                                        className="absolute inset-y-0 left-0 flex items-center pl-3"
                                                        style={{ color: BRAND }}
                                                    >
                                                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                    </span>
                                                ) : null}
                                            </>
                                        )}
                                    </Listbox.Option>
                                ))}

                                {dataList.length === 0 && (
                                    <div className="px-4 py-3 text-sm text-gray-400">No companies yet.</div>
                                )}
                            </Listbox.Options>
                        </Transition>
                    </div>
                )}
            </Listbox>
        </div>
    )
};

export default DropDown;
